import { Directive, ElementRef, forwardRef, HostListener, OnDestroy, Renderer2 } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs';
import { EnergyUnits, EnergyUnitService } from '../services/energy-unit.service';

// A number input whose form control holds kWh (what opportunities store) but shows the consumption unit the user chose.
// The form value never changes with the setting: only what is displayed and typed does.
// <input type="number" energyKwhInput formControlName="peak" />
@Directive({
    selector: 'input[type=number][energyKwhInput]',
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => EnergyKwhInputDirective), multi: true }],
})
export class EnergyKwhInputDirective implements ControlValueAccessor, OnDestroy {
    private _valueInKWh: number | null = null;
    private _onChange: (value: number | null) => void = () => {};
    private _onTouched: () => void = () => {};
    private readonly _subscription: Subscription;

    constructor(private readonly _element: ElementRef<HTMLInputElement>, private readonly _renderer: Renderer2, private readonly _energyUnitService: EnergyUnitService) {
        // Show the same stored value in the new unit
        this._subscription = this._energyUnitService.consumptionUnit$.subscribe(() => this.render());
    }

    ngOnDestroy(): void {
        this._subscription.unsubscribe();
    }

    writeValue(value: number | null | undefined): void {
        this._valueInKWh = value == null || value === ('' as any) ? null : Number(value);
        this.render();
    }

    registerOnChange(fn: (value: number | null) => void): void {
        this._onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this._onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this._renderer.setProperty(this._element.nativeElement, 'disabled', isDisabled);
    }

    @HostListener('input', ['$event.target.value'])
    onInput(text: string): void {
        const typed = parseFloat(text);

        this._valueInKWh = isNaN(typed) ? null : EnergyUnits.displayToKwh(typed, this._energyUnitService.consumptionUnit);
        this._onChange(this._valueInKWh);
    }

    @HostListener('blur')
    onBlur(): void {
        this._onTouched();
    }

    private render(): void {
        const displayed = this._valueInKWh == null ? '' : String(EnergyUnits.kwhToDisplay(this._valueInKWh, this._energyUnitService.consumptionUnit));

        this._renderer.setProperty(this._element.nativeElement, 'value', displayed);
    }
}
